/** Browser-side Ed25519 keypair helpers for publisher signing keys.
 *  Download-only custody: the private key is never persisted by the app. */

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function isEd25519Available(): boolean {
  return typeof crypto !== "undefined" && !!crypto.subtle;
}

export async function generateKeypair(): Promise<{
  publicKeyB64: string;
  privateJwk: JsonWebKey;
  publicKey: CryptoKey;
}> {
  const pair = (await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"]
  )) as CryptoKeyPair;
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const privateJwk = await crypto.subtle.exportKey("jwk", pair.privateKey);
  return { publicKeyB64: bytesToB64(raw), privateJwk, publicKey: pair.publicKey };
}

export async function signPayload(
  privateJwk: JsonWebKey,
  payload: Uint8Array,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "jwk",
    privateJwk,
    { name: "Ed25519" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign({ name: "Ed25519" }, key, payload as BufferSource);
  return new Uint8Array(sig);
}

export function downloadPrivateKey(
  slug: string,
  fingerprint: string,
  privateJwk: JsonWebKey
): void {
  const blob = new Blob([JSON.stringify(privateJwk, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug}-${fingerprint}.key.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
